import type { ReactNode } from 'react';
import { cn } from '@/shared/utils/cn';
import { formatDisplayPrice } from '@/shared/utils/priceFormatter';
import {
  computeDiscountOnProduct,
  computeGstToShow,
  hasComponentDiscount,
} from '@/features/pdp/utils/priceBreakupUtils';
import { formatGemstonePriceBreakupLabel } from '@/features/pdp/utils/productDetailStones';
import type { StoneSpecification } from '@/features/pdp/domain/models/ProductDetail';

interface PriceBreakupProps {
  goldPrice: number | null;
  diamondPrice: number | null;
  diamondPriceAfterDiscount: number | null;
  discountOnDiamondPriceValue: number | null;
  gemstonePrice: number | null;
  presetSolitairePrice: number | null;
  makingChargePrice: number | null;
  makingChargePriceAfterDiscount: number | null;
  makingChargeDiscountValue: number | null;
  gst: number | null;
  price: number | null;
  discountedPrice: number | null;
  discountValue: number | null;
  discountPercent: number | null;
  discountName: string | null;
  flatDiscountPercent: number | null;
  metalType: string;
  hideBreakUp: boolean;
  gemstonesTypeAndCount: string | null;
  stoneSpecifications?: StoneSpecification[] | null;
}

interface PriceBreakupItemProps {
  label: ReactNode;
  value: ReactNode;
  isFirst?: boolean;
  valueClassName?: string;
  wrapLabel?: boolean;
}

function PriceBreakupItem({
  label,
  value,
  isFirst,
  valueClassName,
  wrapLabel,
}: PriceBreakupItemProps) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center border border-[#e2e2e2]',
        isFirst ? 'border-t' : 'border-t-0'
      )}
    >
      <div
        className={cn(
          'min-w-0 flex-1 px-[15px] py-[7px] text-[14.4px] leading-[1.5em] text-black',
          wrapLabel ? 'whitespace-normal' : 'overflow-hidden text-ellipsis whitespace-nowrap'
        )}
      >
        {label}
      </div>
      <div
        className={cn(
          'px-[15px] py-[7px] text-[14.4px] leading-[1.5em] font-semibold text-black',
          valueClassName
        )}
      >
        {value}
      </div>
    </div>
  );
}

export function PriceBreakup({
  goldPrice,
  diamondPrice,
  diamondPriceAfterDiscount,
  discountOnDiamondPriceValue,
  gemstonePrice,
  presetSolitairePrice,
  makingChargePrice,
  makingChargePriceAfterDiscount,
  makingChargeDiscountValue,
  gst,
  price,
  discountedPrice,
  discountValue,
  flatDiscountPercent,
  metalType,
  hideBreakUp,
  gemstonesTypeAndCount,
  stoneSpecifications,
}: PriceBreakupProps) {
  if (hideBreakUp) return null;

  const discountInput = {
    discountValue,
    makingChargeDiscountValue,
    discountOnDiamondPriceValue,
    makingChargePrice,
    makingChargePriceAfterDiscount,
    diamondPrice,
    diamondPriceAfterDiscount,
  };
  const discountOnProduct = computeDiscountOnProduct(discountInput);
  const gstToShow = computeGstToShow(
    gst,
    flatDiscountPercent,
    discountOnProduct,
    hasComponentDiscount(discountInput)
  );

  const gemstoneLabel = formatGemstonePriceBreakupLabel(
    gemstonesTypeAndCount,
    stoneSpecifications
  );

  const rows: Array<{
    key: string;
    label: ReactNode;
    value: ReactNode;
    valueClassName?: string;
    wrapLabel?: boolean;
  }> = [];

  if (goldPrice) {
    rows.push({
      key: 'gold',
      label: metalType || 'Metal',
      value: formatDisplayPrice(goldPrice),
    });
  }

  if (diamondPrice) {
    rows.push({
      key: 'diamond',
      label: 'Diamond',
      value:
        (discountOnDiamondPriceValue ?? 0) > 0 && diamondPriceAfterDiscount !== null ? (
          <>
            <span className="mr-[5px] text-[14.4px] font-normal leading-[1.5em] text-[#666] line-through">
              {formatDisplayPrice(diamondPrice)}
            </span>
            {formatDisplayPrice(diamondPriceAfterDiscount)}
          </>
        ) : (
          formatDisplayPrice(diamondPrice)
        ),
    });
  }

  if (presetSolitairePrice) {
    rows.push({
      key: 'solitaire',
      label: 'Solitaire',
      value: formatDisplayPrice(presetSolitairePrice),
    });
  }

  if (gemstonePrice) {
    rows.push({
      key: 'gemstone',
      label: gemstoneLabel,
      value: formatDisplayPrice(gemstonePrice),
      wrapLabel: gemstoneLabel.includes(','),
    });
  }

  if (makingChargePrice) {
    rows.push({
      key: 'making-charge',
      label: 'Making Charge',
      value:
        (makingChargeDiscountValue ?? 0) > 0 && makingChargePriceAfterDiscount !== null ? (
          <>
            <span className="mr-[5px] text-[14.4px] font-normal leading-[1.5em] text-[#666] line-through">
              {formatDisplayPrice(makingChargePrice)}
            </span>
            {formatDisplayPrice(makingChargePriceAfterDiscount)}
          </>
        ) : (
          formatDisplayPrice(makingChargePrice)
        ),
    });
  }

  if ((flatDiscountPercent ?? 0) > 0 && discountOnProduct > 0) {
    rows.push({
      key: 'discount',
      label: 'Discount',
      value: `- ${formatDisplayPrice(discountOnProduct)}`,
      valueClassName: 'text-[#007236]',
    });
  }

  if (gstToShow !== null) {
    rows.push({
      key: 'gst',
      label: 'GST',
      value: formatDisplayPrice(gstToShow),
    });
  }

  rows.push({
    key: 'total',
    label: 'Total',
    value: discountValue ? (
      <>
        {price !== null && (
          <span className="mr-[5px] text-[14.4px] font-normal leading-[1.5em] text-[#666] line-through">
            {formatDisplayPrice(price)}
          </span>
        )}
        {discountedPrice !== null && formatDisplayPrice(discountedPrice)}
      </>
    ) : price !== null ? (
      formatDisplayPrice(price)
    ) : null,
  });

  return (
    <div className="text-[12px]">
      <h3 className="mb-2.5 text-sm font-bold leading-5 text-black">Price Break-up</h3>
      <div>
        {rows.map((row, index) => (
          <PriceBreakupItem
            key={row.key}
            isFirst={index === 0}
            label={row.label}
            value={row.value}
            valueClassName={row.valueClassName}
            wrapLabel={row.wrapLabel}
          />
        ))}
      </div>
    </div>
  );
}
